/**
 * Sonlar (demo `Numbers`/`numGame`): son oʻrganilayotgan tilda aytiladi — oʻquvchi raqamda yozadi.
 * 10 ta son, 3 daraja (vaqt va xonalar soni); taymer son aytib boʻlingach boshlanadi, qayta eshitishda toʻxtamaydi.
 */
import { useEffect, useRef, useState } from 'react';
import { LANG, LN } from '../../data';
import { S, useStore } from '../../store';
import { nav } from '../../lib/nav';
import { boop, chime } from '../../lib/sound';
import { hush, sayNumber } from '../../lib/speech';
import { haptic } from '../../lib/telegram';
import { applyCourse, fire, sync } from '../../lib/sync';
import { XPR, estXp } from '../../lib/xp';
import { t, tn } from '../../i18n';
import { Ic } from '../../ui/Icon';

type Lv = 'easy' | 'mid' | 'hard';
const NUMLV: Record<Lv, { n: string; sec: number; dig: [number, number]; c: string }> = {
  easy: { n: 'Oson', sec: 10, dig: [4, 4], c: 'success' },
  mid: { n: 'Oʻrtacha', sec: 8, dig: [4, 5], c: 'info' },
  hard: { n: 'Qiyin', sec: 5, dig: [5, 6], c: 'peach' },
};
const NUMR = 10;
const fmtN = (n: number) => n.toLocaleString('ru');

interface Res { n: number; v: string; good: boolean }

export function Numbers() {
  const [lv, setLv] = useState<Lv | null>(null);
  const [round, setRound] = useState(0);
  if (lv) return <NumGame key={round} lv={lv} again={() => setRound((r) => r + 1)} exit={() => { hush(); setLv(null); }} />;
  return <NumMenu pick={setLv} />;
}

/** Daraja tanlash menyusi (rekordlar bilan). */
function NumMenu({ pick }: { pick: (l: Lv) => void }) {
  const best = useStore((s) => s.bestNum) ?? {};
  return (
    <>
      <div className="sbar">
        <button className="x" aria-label={t('Orqaga')} onClick={() => nav.pop()}><Ic n="back" /></button>
        <b style={{ flex: 1, textAlign: 'left', color: 'var(--ink)', fontSize: 16 }}>{t('Sonlar')}</b>
        <span className="soon" style={{ background: 'var(--primary-soft)', color: 'var(--primary-ink)' }}>{LN.code}</span>
      </div>
      <div className="sbody fade" style={{ gap: 14 }}>
        <section className="numhero">
          <span className="nh-digits">4 827</span>
          <div className="col g4">
            <b style={{ fontSize: 20 }}>{t('Eshiting va raqamda yozing')}</b>
            <span style={{ fontSize: 13, fontWeight: 600, opacity: 0.9 }}>
              {t(LANG === 'ru' ? 'Son rus tilida aytiladi.' : 'Son ingliz tilida aytiladi.')} {tn('# ta son, vaqt cheklangan.', NUMR)}
            </span>
          </div>
        </section>
        <span className="cap">{t('Qiyinlikni tanlang')}</span>
        {(Object.entries(NUMLV) as Array<[Lv, (typeof NUMLV)[Lv]]>).map(([k, l]) => (
          <button key={k} className="qa2" onClick={() => pick(k)}>
            <span className="sq2 numsec" style={{ background: `var(--${l.c}-soft)`, color: `var(--${l.c}-ink)` }}><b>{l.sec}</b><small>s</small></span>
            <span className="grow col g4" style={{ textAlign: 'left' }}>
              <b className="t15b">{t(l.n)}</b>
              <span className="t13">{tn('# soniya', l.sec)} · {l.dig[0] === l.dig[1] ? tn('# xonali sonlar', l.dig[0]) : tn('#–# xonali sonlar', l.dig[0], l.dig[1])}</span>
            </span>
            {best[k] != null && <span className="t12" style={{ fontWeight: 800, color: `var(--${l.c}-ink)` }}>{tn('Rekord #/#', best[k], NUMR)}</span>}
            <span className="chev2"><Ic n="chevRight" size={16} /></span>
          </button>
        ))}
        <p className="t12" style={{ margin: 0 }}>{t('Taymer son aytib boʻlingach boshlanadi. Sonni yana bir marta eshitish mumkin, lekin vaqt toʻxtamaydi.')}</p>
      </div>
    </>
  );
}

/** Bitta oʻyin: NUMR ta son, keyin natija ekrani. */
function NumGame({ lv, again, exit }: { lv: Lv; again: () => void; exit: () => void }) {
  const L2 = NUMLV[lv];
  const [nums] = useState(() => Array.from({ length: NUMR }, () => {
    const d = L2.dig[0] + Math.floor(Math.random() * (L2.dig[1] - L2.dig[0] + 1));
    return Math.floor(10 ** (d - 1) + Math.random() * 9 * 10 ** (d - 1));
  }));
  const [qi, setQi] = useState(0);
  const [res, setRes] = useState<Res[]>([]);
  const [end, setEnd] = useState<{ isRec: boolean; best: number; xp: number } | null>(null);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const ok = res.filter((r) => r.good).length;

  const finish = (all: Res[]) => {
    const okN = all.filter((r) => r.good).length;
    const bn = S().bestNum ?? {}, prev = bn[lv];
    const isRec = prev == null || okN > prev;
    const best = isRec ? okN : prev;
    S().set({ bestNum: { ...bn, [lv]: best }, right: S().right + okN, wrong: S().wrong + (NUMR - okN) });
    const xp = estXp(Math.min(XPR.gameCap, okN * XPR.gameAns * (lv === 'hard' ? 3 : lv === 'mid' ? 2 : 1)));
    setEnd({ isRec, best, xp });
    if (isRec && okN) chime();
    fire(sync.game({ game: 'numbers', score: okN, level: lv }).then((r) => {
      applyCourse(r.course);
      if (alive.current) setEnd((e) => (e ? { ...e, xp: r.xp } : e));
    }), 'game');
  };

  if (end) {
    return (
      <>
        <div className="content fade" style={{ paddingTop: 34, gap: 16 }}>
          <div className="col g8" style={{ alignItems: 'center', textAlign: 'center' }}>
            <div className="flamebig"><Ic n="hash" /></div>
            <h1 className="h2">{tn('Sonlar: #/#', ok, NUMR)}</h1>
            {end.isRec && ok
              ? <span className="soon" style={{ background: 'var(--success-soft)', color: 'var(--success-ink)', fontSize: 15, padding: '6px 14px' }}>{t('Yangi rekord!')}</span>
              : <span className="soon" style={{ fontSize: 15, padding: '6px 14px' }}>{t(L2.n)} · {tn('# soniya', L2.sec)}</span>}
          </div>
          <div className="stat3 center">
            <div><b style={{ color: 'var(--primary-ink)' }}>+{end.xp}</b><span>XP</span></div>
            <div><b>{ok}/{NUMR}</b><span>{t('toʻgʻri')}</span></div>
            <div><b style={{ color: 'var(--peach-ink)' }}>{end.best}</b><span>{t('rekord')}</span></div>
          </div>
          <div className="learned">
            <div className="cap">{t('Natijalar')}</div>
            {res.map((r, i) => (
              <div key={i} className="between">
                <b style={{ color: r.good ? 'var(--success-ink)' : 'var(--error-ink)' }}>{fmtN(r.n)}</b>
                <span>{r.good ? '✓' : (r.v || '—')}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="sfoot">
          <button className="btn soft" onClick={again}>{t('Yana oʻynash')}</button>
          <button className="btn" onClick={exit}>{t('Darajalar')}</button>
        </div>
      </>
    );
  }

  return (
    <NumQ key={qi} n={nums[qi]!} qi={qi} ok={ok} lv={lv} exit={exit}
      answer={(r) => setRes((a) => [...a, r])}
      next={() => { if (qi + 1 < NUMR) setQi(qi + 1); else finish(res); }} />
  );
}

/** Bitta son: aytish → taymer → tekshirish varagʻi. */
function NumQ({ n, qi, ok, lv, exit, answer, next }: { n: number; qi: number; ok: number; lv: Lv; exit: () => void; answer: (r: Res) => void; next: () => void }) {
  const L2 = NUMLV[lv];
  const [val, setVal] = useState('');
  const [started, setStarted] = useState(false);
  const [p, setP] = useState(1);
  const [verdict, setVerdict] = useState<Res | null>(null);
  const inp = useRef<HTMLInputElement>(null);
  const cont = useRef<HTMLButtonElement>(null);
  const R = useRef({ alive: true, started: false, done: false, t0: 0, iv: 0 as ReturnType<typeof setInterval> | 0, val: '' });

  const check = () => {
    const r = R.current; if (r.done) return; r.done = true; if (r.iv) clearInterval(r.iv);
    const v = r.val.trim(), good = v === String(n);
    inp.current?.blur();
    if (good) { chime(); } else { boop(); haptic.bad(); }
    const res = { n, v, good }; setVerdict(res); answer(res);
    setTimeout(() => cont.current?.focus(), 250);
  };
  const checkRef = useRef(check);
  useEffect(() => { checkRef.current = check; });

  const start = () => {
    const r = R.current; if (!r.alive || r.started || r.done) return;
    r.started = true; r.t0 = Date.now(); setStarted(true);
    r.iv = setInterval(() => {
      const el = (Date.now() - r.t0) / 1000; setP(Math.max(0, 1 - el / L2.sec));
      if (el >= L2.sec) checkRef.current();
    }, 80);
  };

  useEffect(() => {
    const r = R.current; r.alive = true;
    sayNumber(n, start);
    const fo = setTimeout(() => inp.current?.focus(), 50);
    return () => { r.alive = false; clearTimeout(fo); if (r.iv) clearInterval(r.iv); r.iv = 0; r.started = false; };
  }, []); // eslint-disable-line

  const good = verdict?.good;
  return (
    <>
      <div className="sbar">
        <button className="x" aria-label={t('Chiqish')} onClick={exit}><Ic n="x" /></button>
        <div className="trk"><i style={{ width: `${qi / NUMR * 100}%` }} /></div>
        <b>{qi + 1}/{NUMR}</b>
      </div>
      <div className="sbody fade" style={{ gap: 18, alignItems: 'center', paddingTop: 26 }}>
        <span className="soon" style={{ background: `var(--${L2.c}-soft)`, color: `var(--${L2.c}-ink)` }}>{t(L2.n)} · {tn('# soniya', L2.sec)}</span>
        <button className="numplay" aria-label={t('Qayta eshitish')} onClick={() => sayNumber(n, start)}><Ic n="vol" size={44} /></button>
        <span className="t13">{started ? t('Yozing!') : t('Tinglang…')}</span>
        <div className="numtm"><i className={p < 0.35 ? 'low' : undefined} style={{ width: `${p * 100}%` }} /></div>
        <input ref={inp} className={`numin${verdict ? (good ? ' good' : ' bad') : ''}`} inputMode="numeric" pattern="[0-9]*" autoComplete="off"
          placeholder="0000" aria-label={t('Sonni raqamda yozing')} maxLength={7} readOnly={!!verdict} value={val}
          onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 7); R.current.val = v; setVal(v); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (!R.current.done && val) check(); } }} />
        <div className="row g10" style={{ width: '100%' }}>
          <span className="t12 grow">{t('Toʻgʻri:')} <b>{ok}</b></span>
          <span className="t12">{t('Javob — Enter')}</span>
        </div>
      </div>
      <div id="ft">
        {verdict ? (
          <div className={`sheet ${good ? 'ok' : 'bad'}`}>
            <div className="hd">
              <i><Ic n={good ? 'check' : 'x'} style={{ width: 22, height: 22, strokeWidth: 2.6 }} /></i>
              <b>{good ? t('Barakalla!') : verdict.v ? t('Notoʻgʻri') : t('Vaqt tugadi')}</b>
            </div>
            <div className="ans" style={{ fontSize: 30, letterSpacing: '.04em' }}>{fmtN(n)}</div>
            {!good && verdict.v && <p className="msg">{t('Siz yozdingiz:')} {verdict.v}</p>}
            <button ref={cont} className="btn" onClick={next}>{qi + 1 < NUMR ? t('Keyingi son') : t('Natija')}</button>
          </div>
        ) : (
          <div className="sfoot"><button className="btn" onClick={check}>{t('Tekshirish')}</button></div>
        )}
      </div>
    </>
  );
}
